import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useDeleteWizard } from '../../delete/DeleteWizardContext'
import { KEY_LISTS, TRANSACTION_GROUPS, iconFor } from '../import/steps'
import { DELETE_STEPS } from './steps'

/* Step 1, ported from "batchbooks_bulk_delete_synced".

   Same card grid as import and export step 1, driven by the same
   GET /api/import/entities data, so the grouping helpers are reused from the
   import steps module rather than duplicated.

   The old DeleteStartPage's warning about deletion being irreversible is kept
   here — it was the first thing that page said, and it matters more than the
   prototype's silence on the subject. */
export function DeleteSelectSourcePage() {
  const wizard = useDeleteWizard()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'lists' | 'transactions'>(
    wizard.entity?.isTransaction ? 'transactions' : 'lists',
  )

  // Some registry names are a *kind* of another entity — a bundle is an Item, a
  // check is a Purchase. QuickBooks' delete endpoint takes an entity and an id
  // and has nowhere to put the kind filter, so it rejects the name outright.
  // Offering them here only produces that error three steps later, so they are
  // dropped from the picker; the server refuses them too, for a stale client.
  const deletable = wizard.entities.filter((entity) => entity.canDelete)

  const inTab = deletable.filter((entity) =>
    tab === 'transactions' ? entity.isTransaction : !entity.isTransaction,
  )

  let groups: Array<{ heading: string; items: typeof inTab }>
  if (tab === 'lists') {
    groups = [
      { heading: 'Key List', items: inTab.filter((e) => KEY_LISTS.includes(e.name)) },
      { heading: 'Other List', items: inTab.filter((e) => !KEY_LISTS.includes(e.name)) },
    ]
  } else {
    const named = new Set(TRANSACTION_GROUPS.flatMap((g) => g.names))
    groups = [
      ...TRANSACTION_GROUPS.map((group) => ({
        heading: group.heading,
        items: inTab.filter((e) => group.names.includes(e.name)),
      })),
      { heading: 'Other', items: inTab.filter((e) => !named.has(e.name)) },
    ]
  }

  const choose = (name: string) => {
    wizard.setEntityType(name)
    navigate('/app/delete/filter')
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={DELETE_STEPS} current={0} />

      {/* <Alert tone="err" title="Deletion in QuickBooks cannot be undone by us" style={{ marginBottom: 22 }}>
        Once a transaction is removed, only a QuickBooks restore can bring it back. We recommend
        running a <Link to="/app/backup">backup export</Link> first — it costs 0.1 credit per
        transaction.
      </Alert> */}

      <h2>What data do you need to delete?</h2>

      <div className="bb-radios" role="radiogroup" aria-label="Record category">
        <label className="bb-radio">
          <input
            type="radio"
            name="delete_type"
            value="lists"
            checked={tab === 'lists'}
            onChange={() => setTab('lists')}
          />
          Lists
        </label>
        <label className="bb-radio">
          <input
            type="radio"
            name="delete_type"
            value="transactions"
            checked={tab === 'transactions'}
            onChange={() => setTab('transactions')}
          />
          Transactions
        </label>
      </div>

      {groups.map((group) =>
        group.items.length === 0 ? null : (
          <div className="bb-group" key={group.heading}>
            <h3 className="bb-group-h">{group.heading}</h3>
            <div className="bb-types">
              {group.items.map((entity) => (
                <button
                  className={entity.name === wizard.entityType ? 'bb-type bb-type-on' : 'bb-type'}
                  type="button"
                  key={entity.name}
                  aria-pressed={entity.name === wizard.entityType}
                  onClick={() => choose(entity.name)}
                >
                  <span className="bb-type-ico">
                    <BbIcon name={iconFor(entity.name)} />
                  </span>
                  {entity.label ?? entity.name}
                </button>
              ))}
            </div>
          </div>
        ),
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app">
          Cancel
        </Link>
        {wizard.entityType ? (
          <Link className="bb-btn bb-btn-green" to="/app/delete/filter">
            Next Step
            <BbIcon name="arrow-right" />
          </Link>
        ) : null}
      </div>
    </div>
  )
}
